import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector, useLockBodyScroll } from '@/hooks';
import { toggleEditStake } from '@/store';
import { useForm, useFieldArray } from 'react-hook-form';
import { StakeOption, useGetStakesQuery, useUpdateStakesMutation } from '@/store/api/betApi';
import { FaTimes } from 'react-icons/fa';

interface StakeForm {
  chips: StakeOption[];
}

const SetButtonValueModal = () => {
  const { editStakeOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();

  useLockBodyScroll(editStakeOpen);

  const [activeTab, setActiveTab] = useState<'game' | 'casino'>('game');

  const { data: stakeData, isFetching } = useGetStakesQuery();
  const [updateStakes, { isLoading: isUpdating }] = useUpdateStakesMutation();

  const {
    control, register, handleSubmit, reset,
    formState: { errors },
  } = useForm<StakeForm>({ defaultValues: { chips: [] } });

  const { fields } = useFieldArray({ control, name: 'chips' });

  // Populate on open: only real stakes from the API.
  useEffect(() => {
    if (editStakeOpen) {
      reset({ chips: stakeData?.stakeSet ?? [] });
    } else {
      setActiveTab('game');
    }
  }, [editStakeOpen, stakeData, reset]);

  const onSubmit = async (data: StakeForm) => {
    try {
      await updateStakes({ data: data.chips }).unwrap();
      dispatch(toggleEditStake());
    } catch (err) {
      console.error(err);
    }
  };

  if (!editStakeOpen) return null;

  const inputClass =
    'w-full px-3 py-2.5 text-[14px] text-gray-800 border border-gray-300 rounded-md outline-none focus:border-[#0088cc] bg-white';

  return (
    <div className="fixed inset-0 z-[1000] flex justify-center bg-black/60">
      <div className="w-full sm:max-w-[480px] h-full bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-in-from-top">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0" style={{ background: '#0088cc' }}>
          <h2 className="text-white font-bold text-[18px] leading-tight">Set Button Value</h2>
          <button onClick={() => dispatch(toggleEditStake())} aria-label="Close">
            <FaTimes size={18} className="text-white" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-3 pt-3 shrink-0">
          {([
            { key: 'game', label: 'Game Buttons' },
            { key: 'casino', label: 'Casino Buttons' },
          ] as const).map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className="px-5 py-2 text-[14px] font-semibold rounded-t-md transition-colors"
              style={{
                background: activeTab === t.key ? '#2c3e50' : '#d9d9d9',
                color: activeTab === t.key ? '#fff' : '#333',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-2 gap-4 px-4 pt-3 pb-1 shrink-0 border-t border-gray-200">
          <span className="text-[14px] font-bold text-gray-800">Price Label:</span>
          <span className="text-[14px] font-bold text-gray-800">Price Value:</span>
        </div>

        {/* Form rows */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-2 flex flex-col gap-3">
            {isFetching ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="grid grid-cols-2 gap-4">
                  <div className="h-11 rounded-md bg-gray-100 animate-pulse" />
                  <div className="h-11 rounded-md bg-gray-100 animate-pulse" />
                </div>
              ))
            ) : (
              fields.map((field, i) => (
                <div key={field.id} className="grid grid-cols-2 gap-4">
                  {/* Price Label */}
                  <div>
                    <input
                      {...register(`chips.${i}.label` as const, {
                        required: 'Required',
                        maxLength: { value: 6, message: 'Max 6 chars' },
                        validate: (v) => /^[a-zA-Z0-9]*$/.test(v) || 'Alphanumeric only',
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                        },
                      })}
                      placeholder={`Label ${i + 1}`}
                      className={inputClass}
                      style={{ borderColor: errors?.chips?.[i]?.label ? '#fca5a5' : undefined }}
                    />
                    {errors?.chips?.[i]?.label && (
                      <span className="text-[11px] text-red-500">{errors.chips[i]?.label?.message}</span>
                    )}
                  </div>

                  {/* Price Value */}
                  <div>
                    <input
                      type="number"
                      min={1}
                      step="1"
                      inputMode="numeric"
                      {...register(`chips.${i}.value` as const, {
                        valueAsNumber: true,
                        required: 'Required',
                        min: { value: 1, message: 'Min 1' },
                      })}
                      onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                      placeholder="0"
                      className={inputClass}
                      style={{ borderColor: errors?.chips?.[i]?.value ? '#fca5a5' : undefined }}
                    />
                    {errors?.chips?.[i]?.value && (
                      <span className="text-[11px] text-red-500">{errors.chips[i]?.value?.message}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </form>
        </div>

        {/* Footer — Update */}
        <div className="px-4 py-3 shrink-0 bg-white border-t border-gray-200">
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isUpdating}
            className="px-8 py-2.5 rounded-md text-[15px] font-semibold text-white transition-colors disabled:opacity-60"
            style={{ background: '#0088cc' }}
          >
            {isUpdating ? 'Updating…' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetButtonValueModal;
